import {AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '@env/environment';

@Component({
  selector: 'app-trustpilot-mini-widget-dark',
  templateUrl: './trustpilot-mini-widget-dark.component.html',
  styleUrls: ['./trustpilot-mini-widget-dark.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class TrustpilotMiniWidgetDarkComponent implements AfterViewInit {
  private readonly platformId = inject(PLATFORM_ID);
  
  /**
   * Unique token for this widget placement
   * Dark theme widget with light text for dark backgrounds (footer)
   * Note: This can be overridden via @Input if needed
   */
  @Input() token?: string;
  
  /**
   * Show Trustpilot widget based on environment configuration
   */
  protected readonly showWidget = environment.trustpilot?.enabled ?? false;
  
  /**
   * Widget configuration from environment
   */
  protected readonly widgetConfig = environment.trustpilot?.widgets.miniDark ?? {};
  protected readonly locale = environment.trustpilot?.locale ?? 'da-DK';
  protected readonly businessUnitId = environment.trustpilot?.businessunitId ?? '';
  protected readonly reviewPageUrl = environment.trustpilot?.reviewPageUrl ?? '';
  
  /**
   * Get the token to use - either from @Input or from environment config
   */
  protected get widgetToken(): string {
    return this.token ?? this.widgetConfig.token ?? '';
  }
  
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && this.showWidget) {
      this.loadTrustpilotWidget();
    }
  }
  
  private loadTrustpilotWidget(): void {
    // Load Trustpilot script if not already loaded
    if (!document.getElementById('trustpilot-widget-script')) {
      const script = document.createElement('script');
      script.id = 'trustpilot-widget-script';
      script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
      script.async = true;
      document.head.appendChild(script);
      
      script.onload = () => {
        this.refreshWidget();
      };
    } else {
      // Script already loaded, just refresh the widget
      this.refreshWidget();
    }
  }
  
  private refreshWidget(): void {
    // Trustpilot provides a global function to reload widgets
    const trustpilot = (window as any).Trustpilot;
    if (trustpilot && trustpilot.loadFromElement) {
      const trustboxElements = document.querySelectorAll('.trustpilot-widget');
      trustboxElements.forEach(element => {
        trustpilot.loadFromElement(element, true);
      });
    }
  }
}
